import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, debounceTime, map, of, switchMap, withLatestFrom } from 'rxjs';
import { TenantService } from './api/tenant.service';
import { TenantsActions } from './tenants.actions';
import { selectFilters, selectPageSize } from './tenants.selectors';

export const initTenantsEffect = createEffect(
  (actions$ = inject(Actions), svc = inject(TenantService)) => {
    return actions$.pipe(
      ofType(TenantsActions.init),
      switchMap(() =>
        svc.getTenants(0, 20, '', true).pipe(
          map((tenants) => TenantsActions.loadSuccess({ tenants })),
          catchError((error) => of(TenantsActions.loadFailure({ error }))),
        ),
      ),
    );
  },
  { functional: true },
);

export const loadTenantsEffect = createEffect(
  (actions$ = inject(Actions), svc = inject(TenantService)) =>
    actions$.pipe(
      ofType(TenantsActions.load),
      switchMap(({ query, filters, page, size }) =>
        svc.getTenantsPage(page, size, query, true, filters).pipe(
          map(({ tenants, totalElements }) =>
            TenantsActions.loadPageSuccess({ tenants, totalElements, page, size }),
          ),
          catchError((error) => of(TenantsActions.loadFailure({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const createTenantEffect = createEffect(
  (actions$ = inject(Actions), svc = inject(TenantService)) =>
    actions$.pipe(
      ofType(TenantsActions.createTenant),
      switchMap(({ tenantName, tenantDescription, tenantAvatarUrl, firstDayOfWeek }) =>
        svc
          .createTenant({
            tenantName,
            tenantDescription,
            tenantAvatarUrl,
            firstDayOfWeek,
          })
          .pipe(
            map((tenant) => TenantsActions.createTenantSuccess({ tenant })),
            catchError((error) => of(TenantsActions.createTenantFailure({ error }))),
          ),
      ),
    ),
  { functional: true },
);

// No distinctUntilChanged: this effect outlives the page, and `setQuery` sets `loading`, so a
// deduplicated query (e.g. the same search after revisiting the page) would never finish loading.
export const searchTenantsEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(TenantsActions.setQuery),
      debounceTime(300),
      withLatestFrom(store.select(selectFilters), store.select(selectPageSize)),
      map(([{ query }, filters, size]) =>
        TenantsActions.load({ query: query.trim(), filters, page: 0, size }),
      ),
    ),
  { functional: true },
);

export const deleteTenantEffect = createEffect(
  (actions$ = inject(Actions), svc = inject(TenantService)) =>
    actions$.pipe(
      ofType(TenantsActions.deleteTenant),
      switchMap(({ tenantId }) =>
        svc.deleteTenant(tenantId).pipe(
          map(() => TenantsActions.deleteTenantSuccess({ tenantId })),
          catchError((error) => of(TenantsActions.deleteTenantFailure({ error, tenantId }))),
        ),
      ),
    ),
  { functional: true },
);

export const updateTenantEffect = createEffect(
  (actions$ = inject(Actions), svc = inject(TenantService)) =>
    actions$.pipe(
      ofType(TenantsActions.updateTenant),
      switchMap(({ tenantId, changes }) =>
        svc.updateTenant(tenantId, changes).pipe(
          map((tenant) => TenantsActions.updateTenantSuccess({ tenant })),
          catchError((error) => of(TenantsActions.updateTenantFailure({ error, tenantId }))),
        ),
      ),
    ),
  { functional: true },
);

export const createSubscriptionEffect = createEffect(
  (actions$ = inject(Actions), svc = inject(TenantService)) =>
    actions$.pipe(
      ofType(TenantsActions.createSubscription),
      switchMap(({ tenantId, subscriptionName }) =>
        svc.createSubscription(tenantId, { subscriptionName }).pipe(
          map((subscription) =>
            TenantsActions.createSubscriptionSuccess({ tenantId, subscription }),
          ),
          catchError((error) => of(TenantsActions.createSubscriptionFailure({ error, tenantId }))),
        ),
      ),
    ),
  { functional: true },
);
